/**
 * Weekly summary output document
 */
export interface WeeklySummary {
  /** Unique identifier */
  id: string;
  /** Week number (ISO week) */
  week: number;
  /** Year */
  year: number;
  /** File path in OneDrive */
  filePath: string;
  /** OneDrive web URL for opening in browser */
  webUrl?: string;
  /** Raw markdown content */
  content: string;
  /** Parsed sections */
  sections: WeeklySummarySections;
  /** When the summary was created */
  createdUtc: string;
  /** When the summary was last updated */
  updatedUtc: string;
}

export interface WeeklySummarySections {
  summary?: string;
  highlights: string[];
  impact: string[];
  nextSteps: string[];
}

/**
 * Pending weekly proposal awaiting user approval
 * Path: /Apps/progress-agent/state/{taskId}.json -> weeklyProposal
 */
export interface WeeklyProposal {
  proposalData: {
    docPath: string;
    proposal: string;
    exists: boolean;
    webUrl?: string;
  };
  suggestedMode: 'append' | 'replace';
}

/**
 * Output file metadata from OneDrive
 */
export interface OutputFile {
  id: string;
  name: string;
  webUrl: string;
  createdDateTime: string;
  lastModifiedDateTime: string;
  size: number;
}

/**
 * Parse a weekly summary markdown file into structured sections
 */
export function parseWeeklySummary(markdown: unknown): WeeklySummarySections {
  const sections: WeeklySummarySections = {
    highlights: [],
    impact: [],
    nextSteps: [],
  };

  // Ensure we have a string to work with
  if (typeof markdown !== 'string') {
    console.warn('[parseWeeklySummary] Expected string but got:', typeof markdown);
    return sections;
  }

  // Simple section parsing - look for ## headers
  const summaryMatch = markdown.match(/##\s*Summary\s*\n([\s\S]*?)(?=\n##|$)/i);
  if (summaryMatch) {
    sections.summary = summaryMatch[1].trim();
  }

  const highlightsMatch = markdown.match(/##\s*Highlights\s*\n([\s\S]*?)(?=\n##|$)/i);
  if (highlightsMatch) {
    sections.highlights = extractListItems(highlightsMatch[1]);
  }

  const impactMatch = markdown.match(/##\s*Impact\s*\n([\s\S]*?)(?=\n##|$)/i);
  if (impactMatch) {
    sections.impact = extractListItems(impactMatch[1]);
  }

  const nextStepsMatch = markdown.match(/##\s*Next\s*Steps?\s*\n([\s\S]*?)(?=\n##|$)/i);
  if (nextStepsMatch) {
    sections.nextSteps = extractListItems(nextStepsMatch[1]);
  }

  return sections;
}

function extractListItems(text: string): string[] {
  return text
    .split('\n')
    .map(line => line.replace(/^[-*•]\s*/, '').trim())
    .filter(line => line.length > 0);
}



